import { colorsPalettes } from "@similarweb/styles";

import { translate } from "app/filters/i18nFilter";
import { MmxTrafficSource } from "app/pages/website-analysis/traffic-sources/mmx/components/common/types";
import type { DeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import type { ISiteLegendItem } from "../ChannelAnalysisChart/ChannelAnalysisChartContainer";

const { midnight, blue, sky } = colorsPalettes;

export enum TrafficSource {
    Direct = "Direct",
    Mail = "Email",
    Affiliates = "Affiliates",
    Referrals = "Referrals",
    GenAI = "Gen AI",
    PaidSocial = "Social - Paid",
    OrganicSocial = "Social - Organic",
    OrganicSearch = "Search - Organic",
    PaidSearch = "Search - Paid",
    PaidReferrals = "Display Ads",
}

export interface ITrafficSources {
    [key: string]: number;
    [TrafficSource.Direct]: number;
    [TrafficSource.Mail]: number;
    [TrafficSource.Affiliates]: number;
    [TrafficSource.Referrals]: number;
    [TrafficSource.GenAI]: number;
    [TrafficSource.PaidSocial]: number;
    [TrafficSource.OrganicSocial]: number;
    [TrafficSource.OrganicSearch]: number;
    [TrafficSource.PaidSearch]: number;
    [TrafficSource.PaidReferrals]: number;
}

export interface IChannelsData {
    [key: string]: {
        trafficSources: ITrafficSources;
        totalVisits: number;
    };
}

type TotalTrafficDistributionBySource = Record<TrafficSource, number>;

type TrafficDistributionBySource = Record<TrafficSource, number>;

export interface ITotalDeviceData {
    [domain: string]: TotalTrafficDistributionBySource;
}

export interface IDomainsDataSources {
    [key: string]: number;
}

export type TrafficDataByDeviceType = Record<DeviceType, ITotalDeviceData>;

export type IndustryDataByDeviceType = Record<"Total", TotalTrafficDistributionBySource>;

export interface IIndustryData {
    [industry: string]: TrafficDistributionBySource;
}

export interface IDomainsData {
    [TrafficSource.Direct]?: IDomainsDataSources;
    [TrafficSource.Mail]?: IDomainsDataSources;
    [TrafficSource.Affiliates]?: IDomainsDataSources;
    [TrafficSource.Referrals]?: IDomainsDataSources;
    [TrafficSource.GenAI]?: IDomainsDataSources;
    [TrafficSource.PaidSocial]?: IDomainsDataSources;
    [TrafficSource.OrganicSocial]?: IDomainsDataSources;
    [TrafficSource.OrganicSearch]?: IDomainsDataSources;
    [TrafficSource.PaidSearch]?: IDomainsDataSources;
    [TrafficSource.PaidReferrals]?: IDomainsDataSources;
}

export interface IData {
    key: string;
    y: number;
    color?: string;
}
export interface ISeries {
    data: IData[];
    id: string;
    name: string;
    color?: string;
    isActive?: boolean;
}

export interface ILegendItem extends Partial<ISiteLegendItem> {
    isCategory?: boolean;
    data: number;
    visible: boolean;
}

export const TrafficSourceDisplayMap: { [key in TrafficSource]: { text: string; color: string } } = {
    [TrafficSource.Direct]: {
        get text() {
            return translate("utils.direct");
        },
        color: midnight["500"],
    },
    [TrafficSource.OrganicSearch]: {
        get text() {
            return translate("utils.search.organic");
        },
        color: blue["600"],
    },
    [TrafficSource.PaidSearch]: {
        get text() {
            return translate("utils.search.paid");
        },
        color: blue["500"],
    },
    [TrafficSource.Referrals]: {
        get text() {
            return translate("utils.referrals");
        },
        color: blue["400"],
    },
    [TrafficSource.PaidReferrals]: {
        get text() {
            return translate("utils.displayads");
        },
        color: blue["350"],
    },
    [TrafficSource.OrganicSocial]: {
        get text() {
            return translate("utils.social.organic");
        },
        color: blue["300"],
    },
    [TrafficSource.PaidSocial]: {
        get text() {
            return translate("utils.social.paid");
        },
        color: sky["200"],
    },
    [TrafficSource.Affiliates]: {
        get text() {
            return translate("utils.affiliates");
        },
        color: blue["200"],
    },
    [TrafficSource.GenAI]: {
        get text() {
            return translate("utils.gen.ai");
        },
        color: blue["100"],
    },
    [TrafficSource.Mail]: {
        get text() {
            return translate("utils.email");
        },
        color: midnight["50"],
    },
};

export const TotalTrafficSourceDisplayMap: Record<string, { text: string; color: string }> = {
    [MmxTrafficSource.Direct]: {
        get text() {
            return translate("utils.direct");
        },
        color: midnight["500"],
    },
    [MmxTrafficSource.OrganicSearch]: {
        get text() {
            return translate("utils.search.organic");
        },
        color: blue["600"],
    },
    [MmxTrafficSource.PaidSearch]: {
        get text() {
            return translate("utils.search.paid");
        },
        color: blue["500"],
    },
    [MmxTrafficSource.Referrals]: {
        get text() {
            return translate("utils.referrals");
        },
        color: blue["400"],
    },
    [MmxTrafficSource.Display]: {
        get text() {
            return translate("utils.displayads");
        },
        color: blue["350"],
    },
    [MmxTrafficSource.SocialOrganic]: {
        get text() {
            return translate("utils.social.organic");
        },
        color: blue["300"],
    },
    [MmxTrafficSource.SocialPaid]: {
        get text() {
            return translate("utils.social.paid");
        },
        color: sky["200"],
    },
    [MmxTrafficSource.GenAi]: {
        get text() {
            return translate("utils.gen.ai");
        },
        color: blue["100"],
    },
    [MmxTrafficSource.Email]: {
        get text() {
            return translate("utils.email");
        },
        color: midnight["50"],
    },
    [MmxTrafficSource.Affiliates]: {
        get text() {
            return translate("utils.affiliates");
        },
        color: sky["100"],
    },
};
